import React from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { Link } from 'react-router-dom';

const TaxSavingTips = () => {

  const taxSavingTips = [
    {
      title: "Maximize Section 80C Deductions",
      description: "Invest up to ₹1.5 lakh in tax-saving instruments like ELSS, PPF, NSC, and life insurance premiums.",
      details: [
        "ELSS Mutual Funds: 3-year lock-in, potential for higher returns",
        "PPF: 15-year lock-in, guaranteed returns, tax-free maturity",
        "NSC: 5-year lock-in, guaranteed returns",
        "Life Insurance Premiums: Term and whole life policies",
        "EPF Contributions: Employee Provident Fund contributions"
      ],
      savings: "Up to ₹1.5 lakh deduction"
    },
    {
      title: "Health Insurance Benefits (Section 80D)",
      description: "Claim deductions for health insurance premiums for yourself, spouse, children, and parents.",
      details: [
        "Self, Spouse & Children: Up to ₹25,000",
        "Parents (below 60): Up to ₹25,000",
        "Parents (60+): Up to ₹50,000",
        "Senior Citizens: Up to ₹50,000 for self",
        "Preventive Health Check-up: Up to ₹5,000"
      ],
      savings: "Up to ₹1 lakh deduction"
    },
    {
      title: "House Rent Allowance (HRA)",
      description: "Optimize your HRA benefits by understanding the calculation methods and documentation requirements.",
      details: [
        "Actual HRA received",
        "Rent paid minus 10% of basic salary",
        "50% of basic salary (metro cities) or 40% (non-metro)",
        "Keep rent receipts and rental agreement",
        "PAN of landlord if rent exceeds ₹1 lakh annually"
      ],
      savings: "Significant tax savings on rent"
    },
    {
      title: "Home Loan Benefits",
      description: "Leverage home loan benefits for both principal and interest payments.",
      details: [
        "Principal Repayment: Up to ₹1.5 lakh under Section 80C",
        "Interest Payment: Up to ₹2 lakh under Section 24",
        "Additional Interest: Up to ₹1.5 lakh under Section 80EEA",
        "First-time homebuyers get additional benefits",
        "Keep all loan documents and certificates"
      ],
      savings: "Up to ₹5 lakh deduction"
    },
    {
      title: "Education Loan Benefits (Section 80E)",
      description: "Claim deductions for education loan interest payments without any upper limit.",
      details: [
        "Interest on education loan for higher education",
        "No upper limit on deduction amount",
        "Available for 8 years from loan start",
        "Covers self, spouse, children, and legal guardians",
        "Keep loan statements and certificates"
      ],
      savings: "Unlimited interest deduction"
    },
    {
      title: "NPS Contributions (Section 80CCD)",
      description: "Invest in National Pension System for additional tax benefits beyond Section 80C.",
      details: [
        "Additional deduction: Up to ₹50,000 under Section 80CCD(1B)",
        "Employer contribution: Up to 10% of salary under Section 80CCD(2)",
        "Self-employed: Up to 20% of gross income",
        "Long-term retirement planning",
        "Flexible contribution amounts"
      ],
      savings: "Up to ₹50,000 additional deduction"
    }
  ];

  const additionalTips = [
    {
      title: "Donation Benefits (Section 80G)",
      description: "Donate to approved charities and NGOs for tax deductions."
    },
    {
      title: "Medical Expenses (Section 80DDB)",
      description: "Claim deductions for medical treatment of specified diseases."
    },
    {
      title: "Interest on Savings (Section 80TTA)",
      description: "Claim up to ₹10,000 deduction on savings account interest."
    },
    {
      title: "Standard Deduction",
      description: "₹50,000 standard deduction for salaried individuals."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Tax Saving Tips – Maximize Your Tax Savings Legally"
        description="Learn effective tax saving strategies to reduce your tax burden legally. Discover Section 80C, 80D, HRA, and other tax saving options. Complete guide to tax planning in India."
        keywords="tax saving tips, tax deductions, Section 80C, 80D, HRA, tax planning, income tax savings, tax saving investments India"
        type="article"
        articleSection="Tax Planning"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Guides', url: '/guides' },
          { name: 'Tax Saving Tips', url: '/guides/tax-saving-tips' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Tax Saving Tips', url: '/guides/tax-saving-tips' }
        ]}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Tax Saving Tips – Maximize Your Tax Savings Legally
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn effective strategies to reduce your tax burden legally. Discover various tax saving options 
            and maximize your deductions under different sections of the Income Tax Act.
          </p>
        </div>

        {/* Introduction */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Tax Planning Matters
          </h2>
          <p className="text-gray-600 mb-4">
            Tax planning is not about evading taxes but about legally minimizing your tax liability. 
            By understanding various tax saving options available under the Income Tax Act, you can 
            significantly reduce your tax burden while building wealth for the future.
          </p>
          <p className="text-gray-600">
            The key is to start early, plan systematically, and make informed decisions about your 
            investments and expenses to maximize tax benefits.
          </p>
        </div>

        {/* Main Tax Saving Tips */}
        <div className="space-y-8 mb-12">
          {taxSavingTips.map((tip, index) => (
            <div key={index} className="card">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💰</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {tip.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {tip.description}
                  </p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Key Points:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {tip.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start">
                          <span className="text-green-500 mr-2">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <span className="font-semibold text-green-800">
                      Potential Savings: {tip.savings}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Tips */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Additional Tax Saving Options
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {additionalTips.map((tip, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                <p className="text-sm text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tax Calculator CTA */}
        <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Calculate Your Tax Savings
            </h2>
            <p className="text-gray-600 mb-6">
              Use our tax calculator to see how much you can save with different tax saving strategies.
            </p>
            <Link
              to="/calculators/tax"
              className="btn-primary text-lg px-8 py-3 rounded-xl"
            >
              Calculate Tax Savings
            </Link>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/guides/investment-strategies"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Investment Strategies for Wealth Building
              </h3>
              <p className="text-gray-600 text-sm">
                Learn how to build wealth through smart investment strategies and long-term planning.
              </p>
            </Link>
            <Link
              to="/calculators/sip"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                SIP Calculator for Tax Saving
              </h3>
              <p className="text-gray-600 text-sm">
                Calculate returns on your tax-saving SIP investments and plan your financial future.
              </p>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TaxSavingTips;
